"use client";

import { useEffect } from "react";
import { X } from "lucide-react";
import { Link } from "@/i18n/routing";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslations } from "next-intl";

interface CookiePolicyPopupProps {
  onClose: () => void;
  onManagePreferences: () => void;
}

export default function CookiePolicyPopup({
  onClose,
  onManagePreferences,
}: CookiePolicyPopupProps) {

  const t = useTranslations("CookieComponents");

  // Prevent background scroll while modal is open
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  // Close on ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <AnimatePresence>
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex justify-between items-center border-b border-gray-200 px-6 py-4 bg-white/80 backdrop-blur-md">
            <h2 className="text-lg font-semibold text-gray-900">{t("CookiePolicyPopup.title")}</h2>
            <button
              onClick={onClose}
              aria-label="Close cookie policy"
              className="p-2 rounded-full hover:bg-gray-100 transition"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto px-6 py-5 text-sm text-gray-700 leading-relaxed space-y-5 max-h-[65vh]">
            <p>
              <strong>{t("CookiePolicyPopup.effective")}</strong>
            </p>

            <p>{t("CookiePolicyPopup.intro", { company: "CISCODE" })}</p>

            <div className="space-y-3">
              <h3 className="text-base font-semibold text-gray-900">{t("CookiePolicyPopup.typesTitle")}</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>{t("CookiePolicyPopup.types.essential.title")}:</strong> {t("CookiePolicyPopup.types.essential.desc")}</li>
                <li><strong>{t("CookiePolicyPopup.types.functional.title")}:</strong> {t("CookiePolicyPopup.types.functional.desc")}</li>
                <li><strong>{t("CookiePolicyPopup.types.analytics.title")}:</strong> {t("CookiePolicyPopup.types.analytics.desc")}</li>
                <li><strong>{t("CookiePolicyPopup.types.marketing.title")}:</strong> {t("CookiePolicyPopup.types.marketing.desc")}</li>
              </ul>
            </div>

            <div>
              <h3 className="text-base font-semibold text-gray-900">{t("CookiePolicyPopup.choicesTitle")}</h3>
              <p>{t("CookiePolicyPopup.choicesDesc")}</p>
            </div>

            <div>
              <h3 className="text-base font-semibold text-gray-900">{t("CookiePolicyPopup.infoTitle")}</h3>
              <p>{t("CookiePolicyPopup.infoDesc")}{" "}
                <Link                   
                    href="/legal?doc=CookiePolicy"
                    className="text-brand-secondary hover:underline"
                    target="_blank"
                    rel="noopener noreferrer">
                  {t("links.cookiePolicy")}
                </Link>{" "}
                {t("and")}{" "}
                <Link 
                    href="/legal?doc=PrivacyPolicy"
                    className="text-brand-secondary hover:underline"
                    target="_blank"
                    rel="noopener noreferrer">
                  {t("links.privacyPolicy")}
                </Link>.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 bg-white/80 backdrop-blur-md px-6 py-4 flex flex-col sm:flex-row justify-end gap-3">
            <button
              onClick={onManagePreferences}
              className="px-5 py-2 text-sm font-medium border border-brand-secondary text-brand-secondary rounded-sm hover:bg-blue-50 transition"
            >
              <span className="font-button font-[number:var(--button-font-weight)] [font-style:var(--button-font-style)]">{t("CookiePolicyPopup.managePreferences")}</span>  
            </button>

            <Link
              href="/legal#cookie-policy"
              className="px-5 py-2 text-sm font-medium bg-brand-gradient text-white rounded-sm transition text-center"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="font-button font-[number:var(--button-font-weight)] [font-style:var(--button-font-style)]">{t("CookiePolicyPopup.readFull")}</span>  
            </Link>
          </div>
        </motion.div>
    </AnimatePresence>
  );
}
